import { CartLineItem } from 'cart/types'
import cloneDeep from 'lodash.clonedeep'
import { getMonthlyPaymentOnAPR } from 'prices/APR.utils'
import { Currency, CurrencyDiscount, CurrencyMap, DiscountedVariants, FormattedPrices, Prices, ProductPriceMap } from 'prices/types'
import { getRegionPod4DefaultKey, getRegionPod4UltraDefaultKey } from 'prices/utils'
import { MattressProducts, PodCoverProducts } from 'products/allProducts'
import { Product, ProductCore, ProductCoreBase, ProductVariant, VariantCore, VariantKey } from 'products/types'
import {
	Collection10PercentOff,
	Collection10PercentOffVariants,
	Collection20PercentOff,
	Collection20PercentOffVariants,
	Collection25PercentOff,
	Collection25PercentOffVariants,
	Collection30PercentOff,
	Collection30PercentOffVariants,
	isPodBedVariantId,
} from 'products/utils'
import { discountCodes } from 'promos'
import { VARIABLE_CURRENCY_MAP } from 'stripe_lib/scripts'
import { RegionShort } from 'utils/internationalization'

export const SUBSCRIPTION_REGION_MAP = {
	'8-standard-membership': {
		'': 10,
		us: 10,
		eu: 11,
		ch: 11,
		mx: 375,
		uk: 9,
		ca: 13,
		au: 16,
		se: 133,
		dk: 93,
		ae: 40,
	},
	'8-pro-membership': {
		'': 17,
		us: 17,
		eu: 17,
		ch: 17,
		mx: 375,
		uk: 17,
		ca: 25,
		au: 25,
		se: 184,
		dk: 117,
		ae: 65,
	},
	'8-plus-membership': {
		'': 25,
		us: 25,
		eu: 25,
		ch: 25,
		uk: 25,
		ca: 34,
		au: 42,
		se: 275,
		dk: 175,
		ae: 99,
		mx: 567,
	},
}

export type SubscriptionKeys = keyof typeof SUBSCRIPTION_REGION_MAP

const discountCartLineByPercent = (item: CartLineItem, percent: number): CartLineItem => {
	const newItem = cloneDeep(item)
	const percentOff = percent / 100
	newItem.price = item.price * (1 - percentOff)
	newItem.comparePrice = item.comparePrice ?? item.price
	return newItem
}

const discountSingleCurrency = (countryPrices: Prices, percent: number): Prices => {
	if (countryPrices.price === null) return countryPrices

	const newPrices = cloneDeep(countryPrices)
	const discountPrice = (parseFloat(newPrices.price ?? '0') * (100 - percent)) / 100
	newPrices.price = discountPrice.toString()
	newPrices.comparePrice = newPrices.comparePrice ?? countryPrices.price
	return newPrices
}

const discountCurrencyMapByPercent = (prices: CurrencyMap, percent: number): CurrencyMap => {
	const pricesCopy = cloneDeep(prices)
	let currencyCode: keyof typeof pricesCopy
	for (currencyCode in pricesCopy) {
		const countryPrices = pricesCopy[currencyCode]
		pricesCopy[currencyCode] = discountSingleCurrency(countryPrices, percent)
	}
	return pricesCopy
}

export class PriceManager {
	private _appliedDiscount: DiscountedVariants | undefined = undefined
	private shouldDiscountAccessories = false
	private allPrices: ProductPriceMap

	constructor(public productPrices: ProductPriceMap) {
		this.allPrices = this.productPrices
	}

	public static podMaxMonthsAtZeroInterest = 36
	public static coverMaxMonthsAtZeroInterest = 24
	public static emptyPrice: CurrencyMap = {
		USD: { price: null, comparePrice: null },
		GBP: { price: null, comparePrice: null },
		EUR: { price: null, comparePrice: null },
		CAD: { price: null, comparePrice: null },
		AUD: { price: null, comparePrice: null },
		SEK: { price: null, comparePrice: null },
		DKK: { price: null, comparePrice: null },
		AED: { price: null, comparePrice: null },
		CHF: { price: null, comparePrice: null },
		MXN: { price: null, comparePrice: null },
	}

	public static getFinancingAmount(dollars: number, region: RegionShort): number {
		if (region === '' || region === 'us' || region === 'ca') {
			return this.getAffirmFinancingAmount(dollars)
		}
		if (region === 'eu') {
			return this.getKlarnaFinancingAmount(dollars)
		}

		return NaN
	}

	public static getAffirmMaxMonths(cents: number, currency?: Currency): number {
		if (currency && !PriceManager.currencyHasAffirm(currency)) {
			return NaN
		}

		let months = 6

		if (cents >= 100 * 100) {
			months = 12
		}
		if (cents > 1495 * 100) {
			months = 24
		}
		if (cents > 2000 * 100) {
			months = 36
		}

		return months
	}

	public static getKlarnaMaxMonths(): number {
		return 3
	}

	public static getAffirmFinancingAmount(price: number): number {
		if (!price) {
			return NaN
		}
		if (price >= 50) {
			const maxMonths = this.getAffirmMaxMonths(price * 100)

			if (maxMonths >= 36) {
				const AFFIRM_INTEREST_MINIMUM = 0.1
				return Math.ceil(getMonthlyPaymentOnAPR(price, AFFIRM_INTEREST_MINIMUM, maxMonths))
			}
			return Math.ceil(price / maxMonths)
		}
		return NaN
	}

	public static getKlarnaFinancingAmount(price: number): number {
		if (!price) return NaN

		return Math.ceil(price / this.getKlarnaMaxMonths())
	}

	public static regionHasAffirm(region: RegionShort): boolean {
		return region === '' || region === 'us' || region === 'ca'
	}

	public static regionHasKlarna(region: RegionShort): boolean {
		return region === 'eu' || region === 'uk'
	}

	public static currencyHasAffirm(currency: Currency): boolean {
		return currency === 'USD' || currency === 'CAD'
	}

	public static currencyHasKlarna(currency: Currency): boolean {
		return currency === 'EUR' || currency === 'GBP'
	}

	public static getRegionCurrency(region: RegionShort): Currency {
		switch (region) {
			case 'ca':
				return 'CAD'
			case 'eu':
				return 'EUR'
			case 'se':
				return 'SEK'
			case 'dk':
				return 'DKK'
			case 'ae':
				return 'AED'
			case 'ch':
				return 'CHF'
			case 'uk':
				return 'GBP'
			case 'au':
				return 'AUD'
			case 'mx':
				return 'MXN'
			default:
				return 'USD'
		}
	}

	public static getCurrencyRegion(currency: Currency): RegionShort {
		switch (currency) {
			case 'AUD':
				return 'au'
			case 'CAD':
				return 'ca'
			case 'EUR':
				return 'eu'
			case 'GBP':
				return 'uk'
			case 'SEK':
				return 'se'
			case 'DKK':
				return 'dk'
			case 'AED':
				return 'ae'
			case 'CHF':
				return 'ch'
			case 'MXN':
				return 'mx'
			default:
				return ''
		}
	}

	public static formatPriceToCurrency(price: number | string, currency: Currency): string {
		if (isNaN(price as number)) {
			return ''
		}

		let currencyFormat = currency
		if (currency === 'CAD' || currency === 'AUD' || !currency) {
			currencyFormat = 'USD'
		}

		const formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currencyFormat,
			currencyDisplay: 'narrowSymbol',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})

		const formatted = formatter.format(price as number)

		return formatted.replace(/kr\s/, 'kr')
	}

	public static formatPriceToCurrencyNoDecimal(price: number | string, currency: Currency): string {
		if (isNaN(price as number)) {
			return ''
		}

		let currencyFormat = currency
		if (currency === 'CAD' || currency === 'AUD' || !currency) {
			currencyFormat = 'USD'
		}

		const formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currencyFormat,
			currencyDisplay: 'narrowSymbol',
			minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
			maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
		})

		const formatted = formatter.format(price as number)

		return formatted.replace(/kr\s/, 'kr')
	}

	public static formatPriceToRegionNoDecimal(price: number, region: RegionShort): string {
		return this.formatPriceToCurrencyNoDecimal(price, this.getRegionCurrency(region))
	}

	private static clonePrices(currency: CurrencyMap): CurrencyMap {
		const result: Partial<CurrencyMap> = {}
		Object.keys(currency).forEach((code) => {
			const prices = currency[code as Currency]
			result[code as Currency] = { ...prices }
		})
		return result as CurrencyMap
	}

	private subtractDiscountInCurrencyMap(originalPrices: CurrencyMap, discounts: CurrencyDiscount): CurrencyMap {
		const newPrices = PriceManager.clonePrices(originalPrices)
		if (!Object.keys(newPrices).length) {
			return PriceManager.emptyPrice
		}

		Object.entries(newPrices).forEach((entry) => {
			const currency = entry[0] as Currency
			const prices = entry[1]
			let newPrice = prices.price
			let newComparePrice = prices.comparePrice
			const _discount = discounts[currency]
			if (prices.price !== null && _discount !== null) {
				const _price = parseFloat(prices.price)

				let discountPrice: number = _price

				if (typeof _discount === 'number') {
					discountPrice = _price - _discount
				} else {
					const percentOff = parseInt(_discount.substr(0, 2)) / 100
					discountPrice = discountPrice * (1 - percentOff)
				}
				newPrice = discountPrice > 0 ? discountPrice.toString() : newPrice
				if (newComparePrice === null) {
					newComparePrice = prices.price
				}
			}
			newPrices[currency].price = newPrice
			newPrices[currency].comparePrice = newComparePrice
		})

		return newPrices
	}

	public static getPreferredCurrency(prices: CurrencyMap, currency: Currency): Currency {
		const localCurrrencyAvailable = 'price' in prices[currency] && prices[currency].price !== null
		if (localCurrrencyAvailable) return currency
		return 'USD'
	}

	public setPrices(prices: ProductPriceMap): void {
		this.productPrices = prices
	}

	public getVariantPrices(prices: ProductPriceMap, variantId: number | string): CurrencyMap {
		if (!Object.keys(prices).length) {
			return PriceManager.emptyPrice
		}

		if (!(variantId in prices)) {
			return PriceManager.emptyPrice
		}

		const variant = prices[variantId]

		if (!Object.keys(variant).length) {
			return PriceManager.emptyPrice
		}

		return variant.prices
	}

	public static formatPrices(prices: CurrencyMap, currency: Currency): FormattedPrices {
		const localPrices = prices[currency]
		const priceCents = localPrices.price !== null ? parseFloat(localPrices.price) * 100 : 0
		const compareCents = localPrices.comparePrice !== null ? parseFloat(localPrices.comparePrice) * 100 : 0
		const formattedPrices: FormattedPrices = {
			priceString: PriceManager.formatPriceToCurrencyNoDecimal(priceCents / 100, currency),
			comparePriceString: compareCents ? PriceManager.formatPriceToCurrencyNoDecimal(compareCents / 100, currency) : '',
			price: priceCents,
			comparePrice: compareCents,
			currency: currency,
		}
		return formattedPrices
	}

	public getFormattedVariantPrices(variantId: number | string, currency: Currency): FormattedPrices {
		const variantPrices = this.getVariantPrices(this.allPrices, variantId)
		return PriceManager.formatPrices(variantPrices, currency)
	}

	public get appliedDiscount(): DiscountedVariants | undefined {
		return this._appliedDiscount
	}

	public set appliedDiscount(discount: DiscountedVariants | undefined) {
		this._appliedDiscount = discount
	}

	public setAccessoriesDiscount(bool: boolean): void {
		this.shouldDiscountAccessories = bool
	}

	public discountCartItem(item: CartLineItem, discounts: CurrencyDiscount, globalDiscounts: CurrencyDiscount | null, currency: Currency): CartLineItem {
		if (this.shouldDiscountAccessories && Collection10PercentOff.includes(item.productId as string)) {
			return discountCartLineByPercent(item, 10)
		}
		if (this.shouldDiscountAccessories && Collection20PercentOff.includes(item.productId as string)) {
			return discountCartLineByPercent(item, 20)
		}
		if (this.shouldDiscountAccessories && Collection25PercentOff.includes(item.productId as string)) {
			return discountCartLineByPercent(item, 25)
		}
		if (this.shouldDiscountAccessories && Collection30PercentOff.includes(item.productId as string)) {
			return discountCartLineByPercent(item, 30)
		}

		const newItem = {
			...item,
		}
		const promoStoreDiscount = discounts[currency]
		const siteWideDiscount = globalDiscounts && globalDiscounts[currency]
		let discountAmount = promoStoreDiscount

		if (siteWideDiscount !== null && promoStoreDiscount === null) {
			discountAmount = siteWideDiscount
		}

		if (siteWideDiscount !== null && discountAmount !== null && siteWideDiscount > discountAmount) {
			discountAmount = siteWideDiscount
		}

		if (discountAmount === null) {
			return item
		}

		if (typeof discountAmount === 'number') {
			newItem.price = item.price - discountAmount * 100
		} else {
			const percentOff = parseInt(discountAmount.substr(0, 2)) / 100
			newItem.price = item.price * (1 - percentOff)
		}
		newItem.comparePrice = item.comparePrice ?? item.price
		return newItem
	}

	public applyDiscountToAll(discountCode: string, globalCode: string | undefined, region: RegionShort): ProductPriceMap {
		const pricesCopy = cloneDeep(this.productPrices)

		let globalDiscount: DiscountedVariants | undefined = undefined
		if (globalCode !== undefined) {
			globalDiscount = discountCodes[globalCode]
		}

		const applyDiscountCodeSavings = (): void => {
			const discountedVariants = discountCodes[discountCode]

			for (const variantId in discountedVariants) {
				const variantDiscounts = discountedVariants[variantId]
				const variant = pricesCopy[variantId]

				if (!(variantId in pricesCopy)) {
					continue
				}

				let finalDiscount = variantDiscounts
				const currentGlobalDiscount = globalDiscount ? globalDiscount[variantId] : undefined
				const currentGlobalDiscountUSD = currentGlobalDiscount ? currentGlobalDiscount['USD'] ?? 0 : 0
				const currentVariantDiscountUSD = variantDiscounts['USD'] ?? 0

				if (currentGlobalDiscount && currentGlobalDiscountUSD > currentVariantDiscountUSD) {
					if (isPodBedVariantId(variantId)) {
						const mattressId = region === 'ca' ? MattressProducts.ThreeLayer.variants['caqueen']?.id ?? '' : MattressProducts.FiveLayerDiscounted.variants['queen']?.id ?? ''
						const coverId = PodCoverProducts.Pod3CoverPerfect.variants['queen']?.id ?? ''

						const getTotalGlobalPodSale = (): number => {
							if (!globalDiscount || globalDiscount[coverId] === undefined || globalDiscount[mattressId] === undefined) {
								return 0
							}
							const currentGlobalCoverSale = (globalDiscount[coverId]?.USD as number) ?? 0
							const currentGlobalMattressSale = (globalDiscount[mattressId]?.USD as number) ?? 0
							return currentGlobalCoverSale + currentGlobalMattressSale
						}

						const totalGlobalPodSale = getTotalGlobalPodSale()

						const getTotalVariantPodSale = (): number => {
							if (discountedVariants[coverId] === undefined || discountedVariants[mattressId] === undefined) {
								return 0
							}
							const currentVariantCoverSale = (discountedVariants[coverId]?.USD as number) ?? 0
							const currentVariantMattressSale = (variantDiscounts['USD'] as number) ?? 0
							return currentVariantCoverSale + currentVariantMattressSale
						}

						const totalVariantPodSale = getTotalVariantPodSale()

						if (totalGlobalPodSale > totalVariantPodSale) {
							finalDiscount = currentGlobalDiscount
						}
					} else {
						finalDiscount = currentGlobalDiscount
					}
				}
				variant.prices = this.subtractDiscountInCurrencyMap(variant.prices, finalDiscount)
			}
		}

		const applyAccessoriesSavings = (): void => {
			if (!this.shouldDiscountAccessories) {
				return
			}
			for (const variantId of Collection10PercentOffVariants) {
				const variant = pricesCopy[variantId]
				variant.prices = discountCurrencyMapByPercent(variant.prices, 10)
			}
			for (const variantId of Collection20PercentOffVariants) {
				const variant = pricesCopy[variantId]
				variant.prices = discountCurrencyMapByPercent(variant.prices, 20)
			}
			for (const variantId of Collection25PercentOffVariants) {
				const variant = pricesCopy[variantId]
				variant.prices = discountCurrencyMapByPercent(variant.prices, 25)
			}
			for (const variantId of Collection30PercentOffVariants) {
				const variant = pricesCopy[variantId]
				variant.prices = discountCurrencyMapByPercent(variant.prices, 30)
			}
		}

		applyDiscountCodeSavings()
		applyAccessoriesSavings()

		return pricesCopy
	}

	public withPrices(product: ProductCore, prices: ProductPriceMap, currency: Currency): Product {
		let centsToIncrease = 0
		if (product.custom?.isCoverMembershipBundle !== undefined) {
			centsToIncrease = 18000
		}
		return {
			...product,
			variants: this.addPricestoVariantList(product.variants, prices, centsToIncrease, currency),
		}
	}

	private addPricestoVariantList(variants: { [key: string]: VariantCore }, _prices: ProductPriceMap, centsToIncrease: number | null = null, currency: Currency): { [key: string]: ProductVariant } {
		let prices = _prices
		if (!Object.keys(prices).length) {
			prices = this.productPrices // fallback to default
		}

		const result: { [key: string]: ProductVariant } = {}

		let variantName: keyof typeof variants
		for (variantName in variants) {
			const _v = variants[variantName]
			if (_v.id === undefined) {
				continue
			}

			const pricesForVariant = prices[_v.id]
			const variantPrices = pricesForVariant.prices
			const localPrices = variantPrices[currency]
			let priceCents = localPrices.price !== null ? parseFloat(localPrices.price) * 100 : 0
			let compareCents = localPrices.comparePrice !== null ? parseFloat(localPrices.comparePrice) * 100 : 0

			if (centsToIncrease !== null) {
				priceCents = priceCents ? priceCents + centsToIncrease : 0
				compareCents = compareCents ? compareCents + centsToIncrease : 0
			}

			const formattedPrices: FormattedPrices = {
				priceString: PriceManager.formatPriceToCurrencyNoDecimal(priceCents / 100, currency),
				comparePriceString: compareCents ? PriceManager.formatPriceToCurrencyNoDecimal(compareCents / 100, currency) : '',
				price: priceCents,
				comparePrice: compareCents,
				currency: currency,
			}

			result[variantName] = { prices: formattedPrices, ..._v, key: variantName }
		}
		return result
	}

	public static usesVAT(currency: Currency): boolean {
		return currency === 'GBP' || currency === 'EUR' || currency === 'SEK' || currency === 'DKK'
	}

	public static getRegionDefaultSizeName(region: RegionShort): string {
		switch (region) {
			case 'eu':
				return 'EU Large Double'
			case 'se':
				return 'EU Large Double'
			case 'dk':
				return 'EU Large Double'
			case 'uk':
				return 'UK King'
			case 'ae':
				return 'AE King'
			case 'au':
				return 'AU Queen'
			case 'ch':
				return 'CH Large Double'
			case 'mx':
				return 'MX Queen'
			default:
				return 'Queen'
		}
	}

	public static getRegionDefaultProductDescription(region: RegionShort, product: ProductCoreBase): string {
		switch (region) {
			case 'eu':
			case 'se':
			case 'dk':
			case 'ae':
			case 'ch':
			case 'mx':
				if (product.id === PodCoverProducts.Pod4.id) {
					return 'The Pod is an intelligent sleep system that can be installed onto any bed to improve your sleep. It uses artificial intelligence to power features such as automatic temperature adjustments which personalize the temperature of each side of the bed based on each individual’s sleep schedule, sleep phases, comfort preferences, and environmental conditions. The Pod’s intelligence also unlocks the vibration & thermal alarm, sleep & health reports, and includes continuous software updates and the automatic release of new features over time. All of these intelligent features, plus a lifetime warranty are accessible through our membership plans which includes two separate users per Pod. Add the Pod Cover to your existing mattress to complete your cooling sleep system. Dual-zone temperature control helps you cool or heat your side of the bed between 55° F and 110° F. The Pod Cover can improve your sleep quality up to 32% with its Invisible Sensing Design™, now with double the amount of sensors to help you track your time in deep sleep, HRV, REM sleep and more. The Pod Cover is a mattress topper trusted by thousands to cure night sweats and unlock a deeper more restful sleep. Shop now to start your sleep fitness journey today.'
				}
				return 'Meet the Pod. The Pod is an intelligent sleep system that improves your sleep. It uses artificial intelligence to power features such as automatic temperature adjustments which personalize the temperature of each side of the bed based on each individual’s sleep schedule, sleep phases, comfort preferences, and environmental conditions. The Pod’s intelligence also unlocks the vibration & thermal alarm, sleep & health reports, and includes continuous software updates and the automatic release of new features over time. All of these intelligent features, plus a lifetime warranty are accessible through our membership plans which includes two separate users per Pod.'
			case 'uk':
				if (product.id === PodCoverProducts.Pod4.id) {
					return 'Meet the Pod. The Pod is an intelligent sleep system that improves your sleep. It uses artificial intelligence to power features such as automatic temperature adjustments which personalize the temperature of each side of the bed based on each individual’s sleep schedule, sleep phases, comfort preferences, and environmental conditions. The Pod’s intelligence also unlocks the vibration & thermal alarm, sleep & health reports, and includes continuous software updates and the automatic release of new features over time. All of these intelligent features, plus a lifetime warranty are accessible through our membership plans which includes two separate users per Pod.'
				}
				return 'Meet the Pod. The Pod is an intelligent sleep system that improves your sleep. It uses artificial intelligence to power features such as automatic temperature adjustments which personalize the temperature of each side of the bed based on each individual’s sleep schedule, sleep phases, comfort preferences, and environmental conditions. The Pod’s intelligence also unlocks the vibration & thermal alarm, sleep & health reports, and includes continuous software updates and the automatic release of new features over time. All of these intelligent features, plus a lifetime warranty are accessible through our membership plans which includes two separate users per Pod.'
			case 'au':
				if (product.id === PodCoverProducts.Pod4.id) {
					return 'Experience the Pod Cover by Eight Sleep—a revolutionary cooling sleep system engineered to cool or warm any bed from 55°F to 110°F. Integrating cutting-edge technology, the Pod 4 Cover stands out as the ultimate solution for optimizing your sleep environment through precise temperature control and advanced sleep tracking. Unmatched Temperature Regulation. The Pod 4 Cover employs a state-of-the-art bed cooling system that seamlessly adjusts to your body’s thermal needs. Whether you struggle with night sweats or simply prefer a cooler sleeping climate, this mattress cooling pad delivers exceptional comfort by maintaining your ideal sleep temperature throughout the night. Choose from a wide range of settings on the mattress temperature control system, from a refreshing chill to soothing warmth, ensuring your bed is always set to your personal comfort zone. Enhanced Sleep Quality Recognized as one of the best sleep solutions on the market, the Pod 4 Cover not only cools but also enhances overall sleep quality. The built-in sleep tracker monitors your sleep stages, providing insights into your nightly rest and helping you understand how different temperatures impact your sleep patterns. This data empowers you to make informed adjustments, promoting deeper, more restorative sleep. Advanced Features for Comfort and Convenience Beyond cooling, the Pod 4 Cover features a dual-zone cooling and heating mattress pad, enabling you to experience a customized sleep climate all year round. The cooling mattress cover is designed to fit seamlessly on any standard bed, offering an easy upgrade to your existing setup without the need for a complete mattress overhaul. Ideal for Temperature Sensitive Sleepers. If you’re searching for a cooling mattress topper that addresses issues like overheating during sleep, the Pod 4 Cover is the perfect choice. It serves as an effective night sweat solution, providing relief from discomfort and interruptions caused by fluctuating body temperatures. Durable and Easy to Maintain Crafted with premium materials, the Pod 4 Cover ensures durability and ease of maintenance. The cooling mattress topper is simple to install and integrates smoothly with your existing bedding, and can be added to your existing bed or mattress to create the optimal cooling mattress. Its intuitive interface makes it easy to control via your smartphone, offering a seamless transition to a smarter sleep system. A Must-Have for Every Home Whether you’re a technology enthusiast eager to experiment with a bed cooling device or someone seeking a simple solution to enhance sleep quality, the Pod 4 Cover offers a comprehensive range of features designed to meet your needs. With its ability to control bed temperature, it stands out as a superior choice among temperature control bedding options. Invest in the Eight Sleep Pod 4 Cover today and transform your bed into a personalized sleep sanctuary. Experience the difference as you enjoy cooler nights and wake up refreshed, ready to tackle the day ahead with newfound energy and vitality. Choose the Pod 4 Cover for a sleep experience that’s tailored to your lifestyle, backed by the assurance of cooling mattress topper reviews and the latest advancements in sleep technology.'
				}
				return 'Get in hot – and cool as low as 55°F with the Eight Sleep Pod 4 Ultra—the most advanced and comprehensive sleep system designed to enhance your rest through superior temperature control and innovative sleep technology. This all-encompassing solution integrates an adjustable base with cutting-edge cooling and heating capabilities, setting a new standard for what a restorative night’s sleep should feel like. Precision Temperature Control. The Pod 4 Ultra features an advanced mattress cooling system, designed to maintain the perfect sleep environment by automatically adjusting temperatures from 55°F to 110°F throughout the night'
			case 'ca':
				if (product.id === PodCoverProducts.Pod4.id) {
					return 'Meet the Pod. The Pod is an intelligent sleep system that improves your sleep. It uses artificial intelligence to power features such as automatic temperature adjustments which personalize the temperature of each side of the bed based on each individual’s sleep schedule, sleep phases, comfort preferences, and environmental conditions. The Pod’s intelligence also unlocks the vibration & thermal alarm, sleep & health reports, and includes continuous software updates and the automatic release of new features over time. All of these intelligent features, plus a lifetime warranty are accessible through our membership plans which includes two separate users per Pod.'
				}
				return 'Get in hot – and cool as low as 55°F with the Eight Sleep Pod 4 Ultra—the most advanced and comprehensive sleep system designed to enhance your rest through superior temperature control and innovative sleep technology. This all-encompassing solution integrates an adjustable base with cutting-edge cooling and heating capabilities, setting a new standard for what a restorative night’s sleep should feel like. Precision Temperature Control. The Pod 4 Ultra features an advanced mattress cooling system, designed to maintain the perfect sleep environment by automatically adjusting temperatures from 55°F to 110°F throughout the night'
			default:
				if (product.id === PodCoverProducts.Pod4.id) {
					return 'Experience the Pod Cover by Eight Sleep—a revolutionary cooling sleep system engineered to cool or warm any bed from 55°F to 110°F. Integrating cutting-edge technology, the Pod 4 Cover stands out as the ultimate solution for optimizing your sleep environment through precise temperature control and advanced sleep tracking. Unmatched Temperature Regulation. The Pod 4 Cover employs a state-of-the-art bed cooling system that seamlessly adjusts to your body’s thermal needs. Whether you struggle with night sweats or simply prefer a cooler sleeping climate, this mattress cooling pad delivers exceptional comfort by maintaining your ideal sleep temperature throughout the night. Choose from a wide range of settings on the mattress temperature control system, from a refreshing chill to soothing warmth, ensuring your bed is always set to your personal comfort zone. Enhanced Sleep Quality Recognized as one of the best sleep solutions on the market, the Pod 4 Cover not only cools but also enhances overall sleep quality. The built-in sleep tracker monitors your sleep stages, providing insights into your nightly rest and helping you understand how different temperatures impact your sleep patterns. This data empowers you to make informed adjustments, promoting deeper, more restorative sleep. Advanced Features for Comfort and Convenience Beyond cooling, the Pod 4 Cover features a dual-zone cooling and heating mattress pad, enabling you to experience a customized sleep climate all year round. The cooling mattress cover is designed to fit seamlessly on any standard bed, offering an easy upgrade to your existing setup without the need for a complete mattress overhaul. Ideal for Temperature Sensitive Sleepers. If you’re searching for a cooling mattress topper that addresses issues like overheating during sleep, the Pod 4 Cover is the perfect choice. It serves as an effective night sweat solution, providing relief from discomfort and interruptions caused by fluctuating body temperatures. Durable and Easy to Maintain Crafted with premium materials, the Pod 4 Cover ensures durability and ease of maintenance. The cooling mattress topper is simple to install and integrates smoothly with your existing bedding, and can be added to your existing bed or mattress to create the optimal cooling mattress. Its intuitive interface makes it easy to control via your smartphone, offering a seamless transition to a smarter sleep system. A Must-Have for Every Home Whether you’re a technology enthusiast eager to experiment with a bed cooling device or someone seeking a simple solution to enhance sleep quality, the Pod 4 Cover offers a comprehensive range of features designed to meet your needs. With its ability to control bed temperature, it stands out as a superior choice among temperature control bedding options. Invest in the Eight Sleep Pod 4 Cover today and transform your bed into a personalized sleep sanctuary. Experience the difference as you enjoy cooler nights and wake up refreshed, ready to tackle the day ahead with newfound energy and vitality. Choose the Pod 4 Cover for a sleep experience that’s tailored to your lifestyle, backed by the assurance of cooling mattress topper reviews and the latest advancements in sleep technology.'
				}
				return 'Get in hot – and cool as low as 55°F with the Eight Sleep Pod 4 Ultra—the most advanced and comprehensive sleep system designed to enhance your rest through superior temperature control and innovative sleep technology. This all-encompassing solution integrates an adjustable base with cutting-edge cooling and heating capabilities, setting a new standard for what a restorative night’s sleep should feel like. Precision Temperature Control. The Pod 4 Ultra features an advanced mattress cooling system, designed to maintain the perfect sleep environment by automatically adjusting temperatures from 55°F to 110°F throughout the night'
		}
	}

	public static getRegionDefaultVariant(region: RegionShort, handle: 'pod-4' | 'pod-4-ultra' = 'pod-4'): VariantKey {
		if (handle === 'pod-4') {
			return getRegionPod4DefaultKey(region)
		}
		return getRegionPod4UltraDefaultKey(region)
	}

	public static getRegionSubscriptionPrice(subscription_id: SubscriptionKeys, region: RegionShort): string {
		return this.formatPriceToRegionNoDecimal(SUBSCRIPTION_REGION_MAP[subscription_id][region], region)
	}

	public static getCurrencySubscriptionPrice(subscription_id: SubscriptionKeys, currency: Currency): string {
		return this.formatPriceToCurrencyNoDecimal(SUBSCRIPTION_REGION_MAP[subscription_id][this.getCurrencyRegion(currency)], currency)
	}

	public static getRegionSubscriptionPriceYearly(subscription_id: SubscriptionKeys, region: RegionShort): string {
		return this.formatPriceToRegionNoDecimal(SUBSCRIPTION_REGION_MAP[subscription_id][region] * 12, region)
	}

	public static getCurrencySubscriptionPriceYearly(subscription_id: SubscriptionKeys, currency: Currency): string {
		return this.formatPriceToCurrencyNoDecimal(SUBSCRIPTION_REGION_MAP[subscription_id][this.getCurrencyRegion(currency)] * 12, currency)
	}

	public static getRegionSubscriptionPriceDaily(subscription_id: SubscriptionKeys, region: RegionShort): string {
		return this.formatPriceToCurrency(SUBSCRIPTION_REGION_MAP[subscription_id][region] / 30, this.getRegionCurrency(region))
	}

	public static getCurrencySubscriptionPriceDaily(subscription_id: SubscriptionKeys, currency: Currency): string {
		return this.formatPriceToCurrency(SUBSCRIPTION_REGION_MAP[subscription_id][this.getCurrencyRegion(currency)] / 30, currency)
	}

	public static convertToCurrency(amount: number, currency: Currency): number {
		if (currency in VARIABLE_CURRENCY_MAP) {
			return VARIABLE_CURRENCY_MAP[currency as keyof typeof VARIABLE_CURRENCY_MAP][amount]
		}

		return amount
	}
}
